import { IconButton } from "@/design-system/IconButton/IconButton";
import { SegmentedControl } from "@/design-system/SegmentedControl/SegmentedControl";
import { cn } from "@/lib/utils";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { REWARD_PLANS, RewardGroup, RewardPlan } from "./rewardPlans";

const SLIDES = REWARD_PLANS;

const AUTOPLAY_MS = 5000;
// Horizontal travel that counts as a swipe rather than a tap or a scroll.
const SWIPE_PX = 40;

const totalRewards = (plan: RewardPlan) =>
  plan.groups.reduce((sum, group) => sum + group.items.length, 0);

export const RewardsCarousel = ({ className }: { className?: string }) => {
  const { t } = useTranslation("objectives");
  const [slide, setSlide] = useState(0);
  // Any interaction stops autoplay for good.
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(
      () => setSlide((s) => (s + 1) % SLIDES.length),
      AUTOPLAY_MS,
    );
    return () => clearInterval(id);
  }, [paused]);

  const go = (index: number) => {
    setPaused(true);
    setSlide((index + SLIDES.length) % SLIDES.length);
  };

  return (
    <div
      className={cn(
        "mx-auto flex w-full max-w-[1120px] flex-col gap-5",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <SegmentedControl
          variant="primary"
          label={t("landing.rewards.planLength")}
          options={SLIDES.map((s, i) => ({
            value: String(i),
            label: t("landing.rewards.games", { count: s.games }),
          }))}
          value={String(slide)}
          onChange={(value) => go(Number(value))}
        />
        <div className="flex gap-2">
          <IconButton
            shape="round"
            aria-label={t("landing.rewards.previous")}
            onClick={() => go(slide - 1)}
            icon={<ChevronLeft className="size-4" />}
            className="size-8 mobile:size-10 border-transparent bg-neutral-800 text-neutral-300 hover:bg-neutral-750"
          />
          <IconButton
            shape="round"
            aria-label={t("landing.rewards.next")}
            onClick={() => go(slide + 1)}
            icon={<ChevronRight className="size-4" />}
            className="size-8 mobile:size-10 border-transparent bg-neutral-800 text-neutral-300 hover:bg-neutral-750"
          />
        </div>
      </div>
      <h3 className="mt-2 text-2xl font-extrabold tracking-[-.02em] tabular-nums mobile:text-[28px]">
        {t("landing.rewards.total", { count: totalRewards(SLIDES[slide]) })}
      </h3>
      {/* Plans sit side by side in one strip that slides as a whole: a single
          transform, so the browser moves pixels instead of repainting. */}
      <div
        className="overflow-hidden"
        onTouchStart={(e) => (touchStartX.current = e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (touchStartX.current === null) return;
          const dx = e.changedTouches[0].clientX - touchStartX.current;
          touchStartX.current = null;
          if (Math.abs(dx) > SWIPE_PX) go(slide + (dx < 0 ? 1 : -1));
        }}
      >
        <div
          className="flex transition-transform duration-300 ease-out motion-reduce:transition-none"
          style={{ transform: `translateX(-${slide * 100}%)` }}
        >
          {SLIDES.map((plan, i) => (
            <div
              key={plan.games}
              aria-hidden={i !== slide}
              inert={i !== slide}
              className="grid w-full shrink-0 grid-cols-2 content-start gap-3 touch-device:grid-cols-4"
            >
              {plan.groups.map((group) => (
                <RewardGroupCard key={group.id} group={group} />
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-center gap-2">
        {SLIDES.map((s, i) => (
          <button
            key={s.games}
            type="button"
            aria-label={t("landing.rewards.goTo", { count: s.games })}
            aria-current={i === slide}
            onClick={() => go(i)}
            className={cn(
              "h-1.5 rounded-[3px] transition-[width,background-color] duration-300",
              i === slide
                ? "w-[22px] bg-primary-action"
                : "w-1.5 bg-neutral-700",
            )}
          />
        ))}
      </div>
    </div>
  );
};

const RewardGroupCard = ({ group }: { group: RewardGroup }) => {
  const { t } = useTranslation("objectives");
  const count = group.value ?? group.items.length;
  return (
    <div
      className={cn(
        "flex min-w-0 flex-col gap-4 rounded-[14px] border border-white/8 bg-neutral-900 p-4 mobile:p-5",
        group.id === "packsAndPicks" && "col-span-2",
      )}
    >
      <p className="text-sm text-neutral-400">
        <Trans
          t={t}
          i18nKey={`landing.rewards.groups.${group.id}`}
          count={count}
          // Numbers keep English formatting in every locale (TRANSLATING.md).
          values={{ value: count.toLocaleString("en-US") }}
          components={[
            <strong className="font-bold tabular-nums text-neutral-100" />,
          ]}
        />
      </p>
      <RewardStack items={group.items} />
    </div>
  );
};

/*
  Overlapping reward art, fanned left to right. Every item but the last gets a
  column of at most `step` px that the art overflows, so the columns shrink and
  the fan tightens when the card is narrower, and the last item shows in full.
  A short stack fans wider so each piece stays recognisable.
*/
const stepFor = (count: number) => (count <= 3 ? 40 : count <= 8 ? 28 : 22);

const RewardStack = ({ items }: { items: string[] }) => (
  <div
    className="grid"
    style={{
      gridTemplateColumns: `${
        items.length > 1
          ? `repeat(${items.length - 1}, minmax(0, ${stepFor(items.length)}px)) `
          : ""
      }max-content`,
    }}
  >
    {items.map((src, i) => (
      <img
        key={i}
        src={src}
        alt=""
        loading="lazy"
        style={{ gridColumnStart: i + 1 }}
        className="row-start-1 h-14 w-auto max-w-none mobile:h-16"
      />
    ))}
  </div>
);
